/**
 * Daily events (งานประจำวัน, data/dailyEvents.ts): at their hours the townsfolk gather and act
 * together. The clock alone starts and ends an event, so the HUD (`dailyEventsAt`) and the World
 * agree on what is on, on the server as on a client.
 *
 * A site of an event that is on is staged only while the player is near it (`NEAR`): its extras
 * come in from the site's `enter` spot and take their places (the stage, a stall, the crowd), or
 * walk the parade's route, its position a pure function of the hour; the zone's townsfolk of the
 * roles the site asks for leave their round and join in. When the event ends they walk off and the
 * townsfolk go back to their day; when the player goes far, the site is put away at once.
 *
 * Extras are townsfolk (`kind: 'ambient'` with a `FolkBrain`) held at a station the World never
 * moves them on from (`actT` stays infinite and the part of the day is kept a step ahead), so they
 * walk, flee a fight and greet a passer-by as townsfolk do, in the manners of the townsperson their
 * role names. What they act is `perform`: one NPC act after another (data/npcActs.ts), set with
 * `setAct`, each held a little past the next beat so the act system never fills a gap.
 *
 * Watching: a player who stays near the stage (or the parade's head) for the event's `watch` seconds
 * is paid its reward once per day it runs, and the talk of the day (its `hook`) goes in the log.
 * Every draw comes from the World's `Rng`, every second from `step`.
 */
import { eventActivity, type ActivityMode } from '../data/modes';
import { modeNow } from './modes';
import {
  DAILY_EVENTS,
  EVENT_ROLES,
  eventOn,
  eventProgress,
  type DailyEventDef,
  type EventRole,
  type EventSite,
  type EventSpot,
  type Placement,
} from '../data/dailyEvents';
import { dayPartAt, type Station } from '../data/folk';
import { FOLK_ROLES, NPC_ACTS, type NpcActId } from '../data/npcActs';
import { DAY_SECONDS } from '../data/time';
import { screenFacing, screenHeading } from '../iso';
import type { GameMap } from '../map';
import { freeAt, nearestFloor } from '../path';
import type { Entity, FolkBrain } from '../world';
import { actOf, clearAct, setAct } from './npcActs';
import type { SystemHost, WorldSystem } from './types';

interface Point {
  x: number;
  y: number;
}

/** How near the player must be for a site to be staged (m). */
export const NEAR = 220;
/** Gap between the people of a parade (m). */
const GAP = 2.4;
/** Share of a parade's time spent stopped at its points (the rest walking). */
const STOPPED = 0.45;
/** A parade ends at its last point this far before the event does (to bow out). */
const PARADE_END = 0.9;
/** Seconds a leaving extra has to get off before being put away anyway. */
const LEAVE_MAX = 40;

interface Spots {
  stage: Point;
  crowd: Point;
  enter: Point;
  stalls: Point[];
  route: Point[];
  /** Cumulative length at each route point. */
  at: number[];
}

interface Actor {
  e: Entity;
  role: EventRole;
  place: Placement;
  /** Order in the parade (route) or at the stalls. */
  k: number;
  spot: Point;
  /** A townsperson of the zone who joined (and goes back to their round after). */
  joined: boolean;
  state: 'going' | 'there' | 'leaving';
  beatT: number;
  leaveT: number;
  station: Station;
}

interface Live {
  def: DailyEventDef;
  site: EventSite;
  spots: Spots;
  actors: Actor[];
  barkT: number;
  /** Who performs: `npc` extras, or (stub, falls back to npc) players. Data/modes.ts. */
  mode: ActivityMode;
}

export class DailyEventsSystem implements WorldSystem {
  readonly name = 'dailyEvents';
  /** Staged sites, by site id. */
  private readonly live = new Map<string, Live>();
  /** Extras of ended events on their way off. */
  private readonly leaving: Actor[] = [];
  /** Events that were on at the last step. */
  private readonly on = new Set<string>();
  /** Seconds watched of each event on, and whether it has paid. */
  private readonly watched = new Map<string, { t: number; paid: boolean }>();
  private booted = false;
  private spotsMap: GameMap | null = null;
  private readonly spotsCache = new Map<string, Spots | null>();

  constructor(private readonly h: SystemHost) {}

  /** The sites staged now, with their people (tests, tools). */
  staged(): readonly { event: string; site: string; people: readonly Entity[]; mode: ActivityMode }[] {
    return [...this.live.values()].map((l) => ({
      event: l.def.id,
      site: l.site.id,
      people: l.actors.map((a) => a.e),
      mode: l.mode,
    }));
  }

  step(dt: number): void {
    const w = this.h.world;
    if (w.interiors.inside) return;
    const zone = w.map.zone;
    const hour = w.hour;
    for (const def of DAILY_EVENTS) {
      const sites = def.sites.filter((s) => s.zone === zone);
      if (!sites.length) continue;
      const on = eventOn(def, hour);
      if (on && !this.on.has(def.id)) {
        this.on.add(def.id);
        this.watched.set(def.id, { t: 0, paid: false });
        if (this.booted) this.h.log('sys', `${def.name} เริ่มแล้ว: ${sites.map((s) => s.name).join(' · ')}`);
      } else if (!on && this.on.has(def.id)) {
        this.on.delete(def.id);
        for (const s of sites) this.close(s.id, false);
      }
      if (!on) continue;
      for (const site of sites) {
        const spots = this.spotsOf(site);
        if (!spots) continue;
        const p = w.player;
        const near = Math.hypot(p.x - spots.stage.x, p.y - spots.stage.y) < NEAR;
        let l = this.live.get(site.id);
        if (!near) {
          if (l) this.close(site.id, true);
          continue;
        }
        if (!l)
          l = this.open(def, site, spots, eventProgress(def, hour) * this.lengthOf(def) < 20 && this.booted);
        this.update(l, dt);
      }
      this.watch(def, dt);
    }
    for (let i = this.leaving.length - 1; i >= 0; i--) {
      const a = this.leaving[i]!;
      a.leaveT += dt;
      this.keep(a, dt);
      if (!a.e.folk!.walking || a.leaveT > LEAVE_MAX || !w.entities.has(a.e.id)) {
        w.entities.delete(a.e.id);
        this.leaving.splice(i, 1);
      }
    }
    this.booted = true;
  }

  /** Real seconds an event lasts. */
  private lengthOf(def: DailyEventDef): number {
    return (((def.to - def.from + 24) % 24 || 24) * DAY_SECONDS) / 24;
  }

  // ───────────────────────── spots ─────────────────────────

  private spotsOf(site: EventSite): Spots | null {
    const m = this.h.world.map;
    if (this.spotsMap !== m) {
      this.spotsMap = m;
      this.spotsCache.clear();
    }
    if (this.spotsCache.has(site.id)) return this.spotsCache.get(site.id)!;
    const pt = (s: EventSpot) => this.resolve(m, s);
    const stage = pt(site.stage);
    const crowd = pt(site.crowd);
    const enter = pt(site.enter);
    const stalls = (site.stalls ?? []).map(pt);
    const route = (site.route ?? []).map(pt);
    let spots: Spots | null = null;
    if (stage && crowd && enter && stalls.every(Boolean) && route.every(Boolean)) {
      const r = route as Point[];
      const at = [0];
      for (let i = 1; i < r.length; i++)
        at.push(at[i - 1]! + Math.hypot(r[i]!.x - r[i - 1]!.x, r[i]!.y - r[i - 1]!.y));
      spots = { stage, crowd, enter, stalls: stalls as Point[], route: r, at };
    }
    this.spotsCache.set(site.id, spots);
    return spots;
  }

  private resolve(m: GameMap, s: EventSpot): Point | null {
    let x: number;
    let y: number;
    if ('lm' in s) {
      const base = s.lm === 'plaza' ? m.plaza : m.landmarks?.[s.lm];
      if (!base) return null;
      x = base.x + (s.dx ?? 0);
      y = base.y + (s.dy ?? 0);
    } else [x, y] = s.at;
    if (x < 1 || y < 1 || x > m.w - 1 || y > m.h - 1) return null;
    if (freeAt(m, x, y)) return { x, y };
    const alt = nearestFloor(m, x, y, 4);
    return alt ? { x: alt[0], y: alt[1] } : null;
  }

  /** A free spot within `r` of `c` (a few tries), else `c`. */
  private around(c: Point, r: number): Point {
    const m = this.h.world.map;
    for (let k = 0; k < 4; k++) {
      const a = this.h.random() * Math.PI * 2;
      const d = r * Math.sqrt(this.h.random());
      const x = c.x + Math.cos(a) * d;
      const y = c.y + Math.sin(a) * d;
      if (freeAt(m, x, y)) return { x, y };
    }
    return { ...c };
  }

  // ───────────────────────── staging ─────────────────────────

  /** Stages a site: its extras, and the townsfolk who join. `fresh`: the event has just begun (they walk in). */
  private open(def: DailyEventDef, site: EventSite, spots: Spots, fresh: boolean): Live {
    const w = this.h.world;
    const act = eventActivity(def.id);
    const mode: ActivityMode = act ? modeNow(w, act) : 'npc';
    const l: Live = { def, site, spots, actors: [], barkT: 4, mode };
    this.live.set(site.id, l);
    let stall = 0;
    let route = 0;
    for (const c of site.cast) {
      const role = EVENT_ROLES[c.role]!;
      for (let n = 0; n < c.n; n++) {
        const k = c.at === 'stall' ? stall++ : c.at === 'route' ? route++ : n;
        const spot = this.placeFor(l, c.at, k);
        const walkIn =
          fresh && c.at !== 'route' && Math.hypot(spots.enter.x - spot.x, spots.enter.y - spot.y) < 90;
        const from = walkIn ? this.around(spots.enter, 3) : spot;
        const look = role.looks[Math.floor(this.h.random() * role.looks.length)]!;
        const e = this.h.add({
          kind: 'ambient',
          x: from.x,
          y: from.y,
          name: role.title,
          title: def.name,
          sprite: look,
          speed: 1.25 + this.h.random() * 0.2,
          radius: look === 'townKid' || look === 'fisherKid' ? 0.2 : 0.28,
        });
        const a = this.actor(e, role, c.at, k, spot, false);
        e.folk = this.brain(role, a.station, spot, walkIn);
        a.state = walkIn ? 'going' : 'there';
        l.actors.push(a);
      }
    }
    // The zone's townsfolk of the asked roles, from near enough.
    if (site.join) {
      const r = site.joinR ?? 60;
      for (const [id, e] of w.folk) {
        const as = site.join[FOLK_ROLES[id]!];
        const f = e.folk;
        if (!as || !f || e.dead || this.isActor(e)) continue;
        if (Math.hypot(e.x - spots.stage.x, e.y - spots.stage.y) > r) continue;
        const role = EVENT_ROLES[as]!;
        const place: Placement =
          as === 'vendor' && spots.stalls.length
            ? 'stall'
            : as === 'fighter' || as === 'monk'
              ? 'stage'
              : 'crowd';
        const spot = this.placeFor(l, place, place === 'stall' ? stall++ : 0);
        const a = this.actor(e, role, place, 0, spot, true);
        f.indoors = false;
        f.station = a.station;
        f.idx = Math.max(-1, f.idx - 1);
        f.walking = true;
        f.tx = spot.x;
        f.ty = spot.y;
        e.path = null;
        e.wander = null;
        l.actors.push(a);
      }
    }
    return l;
  }

  private isActor(e: Entity): boolean {
    for (const l of this.live.values()) if (l.actors.some((a) => a.e === e)) return true;
    return this.leaving.some((a) => a.e === e);
  }

  private placeFor(l: Live, at: Placement, k: number): Point {
    const { spots, site } = l;
    if (at === 'stall' && spots.stalls.length) {
      const s = spots.stalls[k % spots.stalls.length]!;
      return k < spots.stalls.length ? { ...s } : this.around(s, 1.2);
    }
    if (at === 'stage') return this.around(spots.stage, site.stageR);
    if (at === 'route') return { ...(spots.route[0] ?? spots.stage) };
    return this.around(spots.crowd, site.crowdR);
  }

  private actor(
    e: Entity,
    role: EventRole,
    place: Placement,
    k: number,
    spot: Point,
    joined: boolean,
  ): Actor {
    return {
      e,
      role,
      place,
      k,
      spot,
      joined,
      state: 'going',
      beatT: this.h.random() * 2,
      leaveT: 0,
      station: { at: 'plaza', act: role.work ?? 'idle', dur: [1e9, 1e9], lantern: role.lantern },
    };
  }

  private brain(role: EventRole, station: Station, spot: Point, walking: boolean): FolkBrain {
    return {
      id: role.manner,
      fixed: false,
      part: this.h.world.dayPart,
      idx: 0,
      station,
      act: walking ? 'idle' : (role.work ?? 'idle'),
      actT: Infinity,
      walking,
      tx: spot.x,
      ty: spot.y,
      indoors: false,
      gesture: null,
      gestureT: 0,
      greetT: 0,
      barkT: Infinity,
      fleeT: 0,
      scaredT: 0,
      attending: false,
      chatLine: 0,
      chatT: 0,
      sparStep: 0,
      blockT: 0,
      lantern: !!role.lantern,
      homeHeading: 2,
      homeFacing: 1,
    };
  }

  /** Puts a site away: at once (`now`), or its extras walk off and the townsfolk go back to their round. */
  private close(id: string, now: boolean): void {
    const l = this.live.get(id);
    if (!l) return;
    this.live.delete(id);
    const w = this.h.world;
    for (const a of l.actors) {
      const f = a.e.folk;
      this.perform(a, null, 0);
      if (a.joined) {
        if (f) {
          // Back to their round at the next step (not walking, so the World does not arrive them here).
          f.actT = 0;
          f.walking = false;
          f.lantern = false;
          a.e.path = null;
        }
        continue;
      }
      if (now || !f) {
        w.entities.delete(a.e.id);
        continue;
      }
      a.state = 'leaving';
      a.leaveT = 0;
      const out = this.around(l.spots.enter, 3);
      f.walking = true;
      f.tx = out.x;
      f.ty = out.y;
      f.act = 'idle';
      a.e.path = null;
      this.leaving.push(a);
    }
  }

  // ───────────────────────── the event under way ─────────────────────────

  private update(l: Live, dt: number): void {
    const w = this.h.world;
    l.actors = l.actors.filter((a) => w.entities.has(a.e.id) && !!a.e.folk);
    const parade = this.parade(l);
    for (const a of l.actors) {
      this.keep(a, dt);
      const f = a.e.folk!;
      if (a.place === 'route' && !a.joined) {
        this.march(l, a, parade, dt);
        continue;
      }
      if (a.state === 'going' && !f.walking) {
        a.state = 'there';
        f.act = a.role.work ?? 'idle';
      }
      if (a.state !== 'there' || f.walking) continue;
      if (f.fleeT > 0 || f.attending || f.gestureT > 0) continue;
      const target = a.place === 'crowd' ? (parade?.head ?? l.spots.stage) : l.spots.crowd;
      this.turnTo(a.e, target);
      this.beat(a, dt);
    }
    this.barks(l, dt);
  }

  /** What the World must not undo: the station, the part of the day a step ahead, the lantern. */
  private keep(a: Actor, dt: number): void {
    const f = a.e.folk;
    if (!f) return;
    const w = this.h.world;
    f.part = dayPartAt((w.hour + (dt * 24) / DAY_SECONDS) % 24);
    f.actT = Infinity;
    f.station = a.station;
    f.indoors = false;
    f.lantern = !!a.role.lantern && a.state !== 'leaving';
    if (!a.joined) f.barkT = Infinity;
    // Performers keep their mind on the show.
    if (a.place !== 'crowd') f.greetT = Math.max(f.greetT, 5);
    if (a.state === 'going' && !f.walking && a.place !== 'route') {
      // Back to the place after a fright (the World let them walk where they fled).
      if (Math.hypot(a.e.x - a.spot.x, a.e.y - a.spot.y) > 1.5 && f.fleeT <= 0) {
        f.walking = true;
        f.tx = a.spot.x;
        f.ty = a.spot.y;
      }
    }
  }

  /** The parade's head now: how far along the route, and whether it is walking. */
  private parade(l: Live): { s: number; walking: boolean; head: Point } | null {
    const { route, at } = l.spots;
    if (route.length < 2) return null;
    const total = at[at.length - 1]!;
    const u = Math.min(1, eventProgress(l.def, this.h.world.hour) / PARADE_END);
    // Stops at every point (the first and last too), walking between, by the legs' lengths.
    const stops = route.length;
    const stopU = STOPPED / stops;
    const walkU = (1 - STOPPED) / total;
    let t = u;
    for (let i = 0; i < route.length; i++) {
      if (t <= stopU || i === route.length - 1) return { s: at[i]!, walking: false, head: route[i]! };
      t -= stopU;
      const leg = at[i + 1]! - at[i]!;
      if (t <= leg * walkU) {
        const s = at[i]! + t / walkU;
        return { s, walking: true, head: this.along(l.spots, s).p };
      }
      t -= leg * walkU;
    }
    return { s: total, walking: false, head: route[route.length - 1]! };
  }

  /** The point at arc length `s` of the route, and the way it runs there. */
  private along(sp: Spots, s: number): { p: Point; dx: number; dy: number } {
    const { route, at } = sp;
    let i = 0;
    while (i < at.length - 2 && s > at[i + 1]!) i++;
    const a = route[i]!;
    const b = route[i + 1]!;
    const leg = at[i + 1]! - at[i]! || 1;
    const k = Math.max(0, Math.min(1, (s - at[i]!) / leg));
    return {
      p: { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k },
      dx: (b.x - a.x) / leg,
      dy: (b.y - a.y) / leg,
    };
  }

  /** A parade's walker: in file behind the head (two abreast), dancing at the stops. */
  private march(l: Live, a: Actor, parade: ReturnType<DailyEventsSystem['parade']>, dt: number): void {
    if (!parade) return;
    const e = a.e;
    const f = e.folk!;
    const s = Math.max(0, parade.s - Math.floor(a.k / 2) * GAP);
    const { p, dx, dy } = this.along(l.spots, s);
    const side = a.k % 2 === 0 ? 0.9 : -0.9;
    const x = p.x - dy * side;
    const y = p.y + dx * side;
    const moved = Math.hypot(x - e.x, y - e.y);
    f.walking = false;
    f.tx = x;
    f.ty = y;
    e.path = null;
    e.x = x;
    e.y = y;
    a.state = 'there';
    if (parade.walking && moved > 1e-4) {
      e.moving = true;
      // Acts with a walk (the lantern held high) carry on in stride; the rest wait for the stop.
      const strides = !!NPC_ACTS[a.role.acts[0] as NpcActId]?.walk;
      const act = actOf(this.h.world, e);
      if (act && !strides) this.perform(a, null, 0);
      else if (!act && strides) this.beat(a, Infinity);
      this.dir(e, dx, dy);
      return;
    }
    if (moved > 2) return;
    // Stopped: face the crowd side of the street, and play.
    this.turnTo(e, l.spots.crowd);
    this.beat(a, actOf(this.h.world, e) ? dt : Infinity);
  }

  private turnTo(e: Entity, t: Point): void {
    this.dir(e, t.x - e.x, t.y - e.y);
  }

  private dir(e: Entity, dx: number, dy: number): void {
    const l = Math.hypot(dx, dy);
    if (l < 1e-3) return;
    const sf = screenFacing(dx, dy);
    if (Math.abs(sf) > 1e-4) e.facing = sf > 0 ? 1 : -1;
    e.heading = screenHeading(dx, dy);
    e.dirX = dx / l;
    e.dirY = dy / l;
  }

  /** The next beat of the act, when the last one is played out. */
  private beat(a: Actor, dt: number): void {
    if ((a.beatT -= dt) > 0) return;
    const r = a.role;
    a.beatT = r.beat[0] + this.h.random() * (r.beat[1] - r.beat[0]);
    this.perform(a, r.acts[Math.floor(this.h.random() * r.acts.length)]!, a.beatT + 0.5);
  }

  /** Plays an act (data/npcActs.ts id) for `seconds` on the actor; null ends it. */
  private perform(a: Actor, act: string | null, seconds: number): void {
    if (act === null) clearAct(a.e);
    else setAct(this.h.world, a.e, act, seconds);
  }

  /** The performers talk to a player close by now and then. */
  private barks(l: Live, dt: number): void {
    if ((l.barkT -= dt) > 0) return;
    l.barkT = 9 + this.h.random() * 6;
    const p = this.h.world.player;
    const near = l.actors.filter(
      (a) => a.place !== 'crowd' && !a.joined && Math.hypot(a.e.x - p.x, a.e.y - p.y) < 14,
    );
    if (!near.length) return;
    const a = near[Math.floor(this.h.random() * near.length)]!;
    const text = l.def.barks[Math.floor(this.h.random() * l.def.barks.length)]!;
    this.h.emit({ type: 'bark', id: a.e.id, text });
  }

  /** Time near the show; the reward and the day's talk once it is enough. */
  private watch(def: DailyEventDef, dt: number): void {
    const st = this.watched.get(def.id);
    const p = this.h.world.player;
    if (!st || st.paid || p.dead) return;
    let near = false;
    for (const l of this.live.values()) {
      if (l.def !== def) continue;
      const focus = this.parade(l)?.head ?? l.spots.stage;
      if (Math.hypot(p.x - focus.x, p.y - focus.y) < def.watchR) near = true;
    }
    if (!near) return;
    st.t += dt;
    if (st.t < def.watch) return;
    st.paid = true;
    const got = this.h.grantReward(def.reward);
    this.h.notice(`ชม${def.name} ได้รับ ${got}`);
    this.h.log('sys', def.hook);
  }
}
